import React, { useRef, useState, useContext, useEffect, useCallback } from "react";
import { useLocation } from "react-router-dom";
import { ProfileContext } from "../context/ProfileContext";
import { useForm } from "react-hook-form";
import { toast } from "react-toastify";
import Modal from "react-bootstrap/Modal";
import { IoMdCloseCircle } from "react-icons/io";
import jsPDF from "jspdf";

import { useAvatarSession } from "../hooks/consultation/useAvatarSession";
import { useChat } from "../hooks/consultation/useChat";
import { useConsultation } from "../hooks/consultation/useConsultation";
import { useSpeechRecognition } from "../hooks/consultation/useSpeechRecognition";
import { usePdfUpload } from "../hooks/consultation/usePdfUpload";

import Loader from "../components/Loader";
import Header2 from "../components/Header2";
import AvatarVideo from "../components/Consultation/AvatarVideo";
import ChatContainer from "../components/Consultation/ChatContainer";

import { createConsultation, fetchOpenConsultation, getAdminAfterSession, getRecommendedTests, updateCondition, getConditionPromptFromAI } from "../services/consultationService";
import { sendEmailToMe } from "../utils/sendEmailToMe";
import logoImg from "../Assets/Images/site_logo.png";
import { speakHeygenAvatar } from "../services/heygenService";
import { getClosingConsultationMessage, getDoctorsName } from "../utils/consultationHelper";

function Consultation() {
    const { headers, profile, getAllConsultation, allDoctors } = useContext(ProfileContext);
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const consIDFromUrl = queryParams.get("consID");

    const [consultationId, setConsultationId] = useState(consIDFromUrl);
    const [consultation, setConsultation] = useState(!!consIDFromUrl);
    const [isLoading, setIsLoading] = useState(false);
    const [isLoadingSummary, setIsLoadingSummary] = useState(false);

    const [message, setMessage] = useState("");
    const [showCloseConfirmModal, setShowCloseConfirmModal] = useState(false);
    const inputRef = useRef(null);
    const { register, handleSubmit, formState: { errors }, reset } = useForm();

    const { chatMessages, setChatMessages, fetchChat, isLoadingChat } = useChat(consultationId, headers);
    const {
        avatar,
        avatarReady,
        isVideoLoading,
        heygenError,
        init: initAvatar,
        restartLiveAvatarSession,
        stop: stopAvatar,
        speak: speakAvatar,
        processPayment,
        videoLayerRef,
        audioElsRef,
        isSmallScreen,
        setIsSmallScreen,
        hasVideo,
        setHasVideo,
        activeConsultationId,
        setActiveConsultationId
    } = useAvatarSession(headers);

    const {
        sendMessage,
        isResponsePending,
        isCompleted,
        setIsCompleted,
        consultationSections,
        setConsultationSections,
        fetchSummary,
        isAITyping,
        setIsAITyping
    } = useConsultation({ consultationId, userId: profile?._id, headers, speakAvatar });

    const { isListening, toggleMic, micSupported } = useSpeechRecognition(inputRef, setMessage);
    const {
        showPdfUpload,
        handlePdfUploadOpen,
        handlePdfUploadClose,
        handlePdfFileChange,
        handlePdfDragEvents,
        handlePdfDrop,
        handlePdfUploadSubmit,
        pdfFile,
        setPdfFile,
        pdfDragActive,
        isUploadingPdf
    } = usePdfUpload(consultationId, headers, () => fetchChat(consultationId));

    useEffect(() => { if (consIDFromUrl) { loadConsultationData(consIDFromUrl) } }, [consIDFromUrl]);
    useEffect(() => { setActiveConsultationId(consultationId); }, [consultationId, setActiveConsultationId]);

    // useEffect(() => {
    //     if (consultation && !avatarReady && !isVideoLoading && !isCompleted) { initAvatar() }
    // }, [consultation, avatarReady, isVideoLoading, isCompleted, initAvatar]);

    const loadConsultationData = async (id) => {
        setIsLoading(true);
        try {
            const response = await fetchOpenConsultation(id, headers);
            if (response?.data?.consultation?.[0]) {
                const data = response.data.consultation[0];
                setConsultation(true);
                setConsultationId(id);
                setIsCompleted(data.is_completed || false);
                await fetchChat(id);
            }
        } catch (error) {
            console.error("Error loading consultation:", error);
            toast.error("Failed to load consultation data.");
        } finally { setIsLoading(false) }
    };

    const onFormSubmit = async (data) => {
        if (!data.discussion.condition1 && !data.discussion.condition2 && !data.discussion.condition3) {
            toast.error("Please select at least one discussion topic.");
            return;
        }
        setIsLoading(true);
        try {
            const response = await createConsultation({ ...data, language: "ENGLISH" }, headers);
            if (response?.code === 200 && response?.status) {
                toast.success("Consultation started.");
                const newId = response.data._id;
                setConsultationId(newId);
                setConsultation(true);
                await fetchChat(newId);
                restartLiveAvatarSession()
            }
        }
        catch (error) { toast.error(error.response.data.error_description || "Failed to start consultation. Please complete existing sessions.") }
        finally { setIsLoading(false) }
    };

    const handleSendMessage = () => {
        sendMessage(message, setChatMessages);
        setMessage("");
    };
    const speakAndWait = async () => {
        setShowCloseConfirmModal(false);
        const doctorName = getDoctorsName(allDoctors, profile?.primary_care_provider);
        const closingMessage = getClosingConsultationMessage(doctorName);
        const res = await speakAvatar(closingMessage);
        if (res?.duration_ms) { await new Promise((r) => setTimeout(r, res.duration_ms)) }
        return res;
    };

    const handleCloseConsultation = async () => {
        setIsCompleted(true);
        setIsLoadingSummary(true);
        try {
            await speakAndWait();
            await fetchSummary();
            const condition = await getConditionPromptFromAI(consultationId, profile?._id, headers);
            const diagnostic_data = await getAdminAfterSession(consultationId, profile?._id, headers);
            if (condition && diagnostic_data) { await handleAddCondition(condition, diagnostic_data) }
            await getAllConsultation();
            await getRecommendedTests(consultationId, profile?._id, headers);
            await stopAvatar();

            toast.success("Consultation completed successfully.");
        } catch (error) {
            console.error("Error closing consultation:", error);
            toast.error("Failed to fully close consultation.");
        }
        finally { setIsLoadingSummary(false) }
    };

    const handleAddCondition = async (condition, diagnostic_data) => {
        try {
            const responseData = await updateCondition(consultationId, condition, diagnostic_data, headers);
            if (responseData.status) { toast.success(responseData.message || "Condition updated successfully.") }
        }
        catch (error) { console.error("Error updating condition:", error) }
    };

    const handleTypingComplete = (messageId) => {
        setIsAITyping(false);
        setChatMessages((prev) => prev.map((msg) => msg.id === messageId ? { ...msg, text: msg.fullText, isTyping: false } : msg));
    };

    const handleAddNewConsultation = () => {
        setConsultationId(null);
        setConsultation(false);
        setIsCompleted(false);
        setChatMessages([]);
        setConsultationSections([]);
        reset();
        toast.info("Started a new session.");
    };

    const handleDownloadPdf = () => {
        try {
            const doc = new jsPDF();
            const currentDate = new Date().toLocaleDateString();
            const currentTime = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: true });

            doc.setProperties({
                title: "Consultation Summary",
                subject: "DR. Jan AI Consultation",
                author: "DR. Jan AI",
                creator: "DR. Jan AI Platform",
            });

            // Branding Header
            doc.addImage(logoImg, "PNG", 15, 10, 20, 20);
            doc.setFontSize(20);
            doc.setTextColor(0, 101, 255);
            doc.text("DR. Jan AI Consultation", 105, 20, { align: "center" });
            doc.setDrawColor(0, 101, 255);
            doc.setLineWidth(0.5);
            doc.line(20, 25, 190, 25);

            // Patient Info
            doc.setTextColor(0, 0, 0);
            doc.setFontSize(12);
            doc.text("Consultation Summary", 20, 35);
            doc.setFontSize(10);
            doc.text(`Date: ${currentDate}`, 20, 45);
            doc.text(`Time: ${currentTime}`, 20, 50);
            doc.text(`Patient: ${profile?.full_name || "Patient"}`, 20, 55);
            if (profile?.age) doc.text(`Age: ${profile?.age}`, 20, 60);
            if (profile?.gender) doc.text(`Gender: ${profile?.gender}`, 20, 65);

            doc.setDrawColor(200, 200, 200);
            doc.setLineWidth(0.2);
            doc.line(20, 70, 190, 70);

            let yPos = 80;
            if (consultationSections && consultationSections.length > 0) {
                consultationSections.forEach((section, index) => {
                    if (yPos > 240) { doc.addPage(); yPos = 20; }
                    doc.setFontSize(12);
                    doc.setTextColor(0, 101, 255);
                    doc.text(`${index + 1}. ${section.question}`, 20, yPos);
                    yPos += 7;
                    doc.setFontSize(10);
                    doc.setTextColor(0, 0, 0);
                    const answerLines = doc.splitTextToSize(section.answer, 170);
                    doc.text(answerLines, 20, yPos);
                    yPos += answerLines.length * 5 + 15;
                });
            } else {
                doc.setFontSize(10);
                doc.setTextColor(100, 100, 100);
                doc.text("No consultation data available.", 20, yPos);
            }

            // Footers
            const pageCount = doc.internal.getNumberOfPages();
            for (let i = 1; i <= pageCount; i++) {
                doc.setPage(i);
                doc.setFontSize(8);
                doc.setTextColor(100, 100, 100);
                doc.text("This document is generated by DR. Jan AI Consultation Assistant and is for informational purposes only.", 105, 280, { align: "center" });
                doc.text(`Page ${i} of ${pageCount}`, 105, 285, { align: "center" });
            }

            doc.save(`consultation_summary_${new Date().toISOString().split("T")[0]}.pdf`);
            toast.success("PDF summary downloaded successfully!");
        } catch (error) {
            console.error("Download error:", error);
            toast.error("Failed to generate PDF.");
        }
    };

    return (
        <div className="comm_consultant_page hide-scrollbar">
            <style>
                {`
                @media (max-width: 991px) {
                    .consult_ai_chat_main_row { display: block !important; position: relative; }
                    .ai_chat_box_right { width: 100% !important; padding: 0 !important; height: 80vh; display: flex; flex-direction: column; }
                }
                .all-chat-main { -ms-overflow-style: none; scrollbar-width: none; }
                .all-chat-main::-webkit-scrollbar { display: none; }
                `}
            </style>

            <Loader isLoading={isLoading} />
            <div className="comm_page_outer2">
                <Header2 />

                <div className="comm_inner_page_layout">
                    <div className="d-flex consult_ai_chat_main_row">
                        <AvatarVideo
                            avatar={avatar}
                            videoLayerRef={videoLayerRef}
                            isVideoLoading={isVideoLoading}
                            avatarReady={avatarReady}
                            heygenError={heygenError}
                            consultationId={consultationId}
                            isCompleted={isCompleted}
                            initAvatar={initAvatar}
                            stopAvatar={stopAvatar}
                            processPayment={processPayment}
                            setShowCloseConfirmModal={setShowCloseConfirmModal}
                            restartLiveAvatarSession={restartLiveAvatarSession}
                            audioElsRef={audioElsRef}
                            isSmallScreen={isSmallScreen}
                            setIsSmallScreen={setIsSmallScreen}
                            headers={headers}
                            hasVideo={hasVideo}
                            setHasVideo={setHasVideo}
                        />
                        <ChatContainer
                            consultation={consultation}
                            chatMessages={chatMessages}
                            isCompleted={isCompleted}
                            message={message}
                            setMessage={setMessage}
                            sendMessage={handleSendMessage}
                            handleMicToggle={toggleMic}
                            isListening={isListening}
                            isResponsePending={isResponsePending}
                            isAITyping={isAITyping}
                            isLoadingChat={isLoadingChat}
                            onPdfClick={handlePdfUploadOpen}
                            register={register}
                            errors={errors}
                            onFormSubmit={handleSubmit(onFormSubmit)}
                            isLoading={isLoading}
                            profile={profile}
                            onTypingComplete={handleTypingComplete}
                            inputRef={inputRef}
                            micSupported={micSupported}
                            handleDownload={handleDownloadPdf}
                            handleAddNewConsultation={handleAddNewConsultation}
                            handleEmailToMe={() => sendEmailToMe()}
                            consultationSections={consultationSections}
                            isLoadingSummary={isLoadingSummary}
                            isSmallScreen={isSmallScreen}
                        />
                    </div>
                </div>
            </div>

            {/* Modals */}
            <Modal show={showPdfUpload} onHide={handlePdfUploadClose} centered className="comm_modal pdf_upload_modal" style={{ zIndex: 10001 }}>
                <Modal.Body className="p-0">
                    <Modal.Header>
                        <h5 style={{ margin: 0, fontSize: "1.5rem", fontWeight: 600 }}>Upload PDF</h5>
                        <IoMdCloseCircle onClick={handlePdfUploadClose} style={{ cursor: 'pointer', fontSize: '24px' }} />
                    </Modal.Header>
                    <div className="pdf_upload_body p-4">
                        <div
                            className={`pdf_drop_zone ${pdfDragActive ? "active" : ""} ${pdfFile ? "has-file" : ""}`}
                            onDragOver={handlePdfDragEvents}
                            onDragLeave={handlePdfDragEvents}
                            onDrop={handlePdfDrop}
                            onClick={() => document.getElementById('pdf-input').click()}
                            style={{ border: '2px dashed #00A1FB', borderRadius: '12px', padding: '40px', textAlign: 'center', cursor: 'pointer', background: pdfDragActive ? '#f0f9ff' : 'transparent' }}
                        >
                            <input id="pdf-input" type="file" accept=".pdf" onChange={handlePdfFileChange} style={{ display: 'none' }} />
                            {pdfFile ?
                                <div>
                                    <p style={{ fontSize: '16px' }}>{pdfFile.name}</p>
                                    <button style={{ fontSize: '14px' }} className="btn btn-sm btn-danger" onClick={(e) => { e.stopPropagation(); setPdfFile(null); }}>Remove</button>
                                </div>
                                :
                                <p>Drag & Drop or click to browse (Max 10MB)</p>
                            }
                        </div>
                        <div className="mt-4 d-flex gap-2">
                            <button className="main-outline-btn w-100" onClick={handlePdfUploadClose}>Cancel</button>
                            <button className="main-btn w-100" onClick={handlePdfUploadSubmit} disabled={!pdfFile || isUploadingPdf}>
                                {isUploadingPdf ? "Uploading..." : "Upload"}
                            </button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>

            <Modal show={showCloseConfirmModal} onHide={() => setShowCloseConfirmModal(false)} centered className="comm_modal logout_modal" style={{ zIndex: 10001 }}>
                <Modal.Body className="p-0">
                    <Modal.Header>
                        <IoMdCloseCircle onClick={() => setShowCloseConfirmModal(false)} />
                    </Modal.Header>
                    <div className="comm_inner_body_modal">
                        <h4>Are you sure you want to close the consultation?</h4>
                        <div className="modal_pair_btns">
                            <button className="main-outline-btn" onClick={() => setShowCloseConfirmModal(false)}>Cancel</button>
                            <button className="main-btn" style={{ backgroundColor: '#e74c3c', borderColor: '#e74c3c' }} onClick={handleCloseConsultation}>
                                Close Consultation
                            </button>
                        </div>
                    </div>
                </Modal.Body>
            </Modal>
        </div>
    );
}

export default Consultation;
